import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import axios from "axios";
import { API } from "../api";
import { DOC } from "../components/print/DocumentShell";
import { fmt } from "../utils";
import { CompanyMark } from "../branding/companyBrand";

const KIND_LABEL = { sale: "Tax Invoice", purchase: "Purchase Bill", quotation: "Rate Quotation", letter: "Letter" };

// Public, unauthenticated page the QR code on a printed document resolves
// to. Anyone can open this with just the document ID from the QR — so it
// only ever shows the small safe summary the /api/public/verify endpoint
// returns, never the full document.
export default function VerifyDocument() {
  const { kind, id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    axios.get(`${API}/public/verify/${kind}/${id}${window.location.search || ""}`)
      .then((r) => setState({ loading: false, data: r.data, error: false }))
      .catch(() => setState({ loading: false, data: null, error: true }));
  }, [kind, id]);

  const valid = state.data?.valid;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#EEF1F8" }}>
      <div className="w-full max-w-sm rounded-xl border bg-white shadow-lg overflow-hidden" style={{ borderColor: DOC.border }}>
        <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center" style={{ background: `linear-gradient(180deg, ${DOC.canvas} 0%, #FFFFFF 100%)` }}>
          <div className="mb-2">
            <CompanyMark
              company={{
                id: state.data?.company_id || "sgp",
                name: state.data?.issued_by || "Shree Gauri Pharma",
                short_name: state.data?.company_id === "neo" ? "NHB" : state.data?.company_id === "prachi" ? "PE" : "SGP",
              }}
              className="h-12 w-12"
              imageClassName="h-10 w-10"
            />
          </div>
          <p className="font-bold" style={{ color: DOC.deepBlue }}>{state.data?.issued_by || "Document Verification"}</p>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">Document Verification</p>
        </div>

        <div className="px-6 py-6">
          {state.loading && <p className="text-center text-sm text-slate-500">Checking document…</p>}

          {!state.loading && (state.error || !valid) && (
            <div className="flex flex-col items-center text-center gap-2" data-testid="verify-invalid">
              <ShieldAlert className="h-9 w-9 text-rose-500" />
              <p className="font-bold text-rose-600">Not a Recognised Document</p>
              <p className="text-xs text-slate-500">This link does not match a recognised document in the selected company workspace.</p>
            </div>
          )}

          {!state.loading && valid && (
            <div data-testid="verify-valid">
              <div className="flex flex-col items-center text-center gap-1.5 mb-5">
                <ShieldCheck className="h-9 w-9 text-emerald-600" />
                <p className="font-bold text-emerald-700">{state.data.kind === "quotation" ? "Genuine Quotation" : "Genuine Document"}</p>
                <p className="text-xs text-slate-500">{KIND_LABEL[state.data.kind] || "Document"} issued by {state.data.issued_by}</p>
              </div>
              <dl className="space-y-2.5 text-sm">
                <Row label="Ref. No." value={state.data.ref_no} />
                <Row label="Date" value={state.data.date} />
                {state.data.party_name && <Row label="Party" value={state.data.party_name} />}
                {state.data.kind === "quotation" ? (
                  <>
                    <Row label="Document Type" value={state.data.quotation_type || "Rate Quotation"} />
                    {state.data.valid_till && <Row label="Valid Till" value={state.data.valid_till} />}
                    {state.data.tender_ref && <Row label="Tender / Enquiry Ref." value={state.data.tender_ref} />}
                    <Row label="Items Quoted" value={String(state.data.item_count ?? 0)} />
                  </>
                ) : (
                  state.data.kind === "sale" && state.data.grand_total != null && <Row label="Amount" value={fmt(state.data.grand_total)} />
                )}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between border-b pb-2" style={{ borderColor: DOC.border }}>
    <dt className="text-slate-500">{label}</dt>
    <dd className="font-semibold text-right" style={{ color: DOC.ink }}>{value || "-"}</dd>
  </div>
);

